import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import { STORAGE_KEYS, LEGACY_STORAGE_KEYS } from '@shared/constants';

interface PermissionEntry {
  id: string;
  revokedOperations?: string[];
}

interface UserAccessListItem {
  id: string;
  licenseId: string;
  admin?: boolean;
  values?: {
    entity?: number;
    databaseId?: string;
  };
}

@Injectable({
  providedIn: 'root'
})
export class LocalPermissionsService {
  private permissions: PermissionEntry[] | null = null;
  private loadingPromise: Promise<void> | null = null;
  private loadedForUserAccess: string | null = null;
  /** Evita repetir GET @me/access para o mesmo licenseId na sessão */
  private resolvedAccessIdByLicense: Map<string, string> = new Map();

  constructor(private http: HttpClient) {}

  isLoaded(): boolean {
    return !!this.permissions;
  }

  async ensureLoaded(): Promise<void> {
    let currentUserAccess = this.getUserAccess();
    if (!currentUserAccess) {
      currentUserAccess = await this.bootstrapUserAccessFromApi();
    }
    if (!currentUserAccess) {
      // Sem contexto de acesso mesmo após bootstrap: estado vazio e estável.
      this.permissions = [];
      this.loadedForUserAccess = null;
      return;
    }

    if (this.permissions && this.loadedForUserAccess === currentUserAccess) {
      return;
    }
    if (this.loadingPromise) {
      return this.loadingPromise;
    }

    this.loadingPromise = this.loadPermissions(currentUserAccess)
      .finally(() => {
        this.loadingPromise = null;
      });

    return this.loadingPromise;
  }

  hasPagePermission(pageId: string): boolean {
    if (!this.permissions) {
      return false;
    }
    return this.permissions.some(p => p.id === pageId);
  }

  isOperationRevoked(pageId: string, operation: string): boolean {
    if (!this.permissions) {
      return true;
    }
    const permission = this.permissions.find(p => p.id === pageId);
    if (!permission) {
      return true;
    }
    const revoked = permission.revokedOperations || [];
    return revoked.includes(operation);
  }

  clear(): void {
    this.permissions = null;
    this.loadingPromise = null;
    this.loadedForUserAccess = null;
    this.resolvedAccessIdByLicense.clear();
  }

  /**
   * URL oficial: GET {user-accounts}/api/access/{accessId}/permissions
   * (ex.: plataforma-autorizacoes.test.betha.cloud/user-accounts/v0.1/api/access/6915.../permissions)
   * O valor em __pwa_user_access é em geral o licenseId; o path exige o id do documento de acesso.
   */
  private async loadPermissions(userAccess: string): Promise<void> {

    const accessId = await this.resolveAccessId(userAccess);
    if (!accessId) {
      this.permissions = [];
      this.loadedForUserAccess = userAccess;
      return;
    }

    const base = this.getUserAccountsBaseUrl();
    const candidates = [
      `${base}/api/access/${encodeURIComponent(accessId)}/permissions`,
      `${base}/api/access/${encodeURIComponent(accessId)}/permissions?offset=0&limit=500`
    ];

    for (const url of candidates) {
      try {
        const response = await firstValueFrom(this.http.get<unknown>(url));
        const normalized = this.normalizeResponse(response);
        if (normalized) {
          this.permissions = normalized;
          this.loadedForUserAccess = userAccess;
          return;
        }
      } catch (_e) {
        // Tenta o próximo formato (paginação).
      }
    }

    this.permissions = [];
    this.loadedForUserAccess = userAccess;
  }

  private async bootstrapUserAccessFromApi(): Promise<string | null> {
    // Sem token, evitar a chamada não é só otimização: ela resultaria em 401, e o
    // redirect do AuthErrorInterceptorService correria em paralelo com o redirect que
    // o AppComponent/PwaInitializationService já dispara ao detectar ausência de token
    // — dois `window.location.href` concorrentes causam loop authorize↔app (PROF-13326).
    // Fallback de leitura na chave antiga (__pwa_bridge_token) por 1 release — TODO remover
    if (!localStorage.getItem(STORAGE_KEYS.BRIDGE_TOKEN) && !localStorage.getItem(LEGACY_STORAGE_KEYS.BRIDGE_TOKEN) &&
        !localStorage.getItem('__pwa_standalone_token')) {
      return null;
    }

    const base = this.getUserAccountsBaseUrl();
    const listUrl = `${base}/api/suite/users/@me/access`;

    try {
      const list = await firstValueFrom(this.http.get<UserAccessListItem[]>(listUrl));
      const valid = (list || []).filter(a => a.licenseId && !a.values?.databaseId);
      if (!valid.length) {
        return null;
      }

      // 1) Tentar manter continuidade do contexto previamente salvo.
      const existingCtx = this.readUserAccessContext();
      if (existingCtx?.licenseId) {
        const sameLicense = valid.find(a => a.licenseId === existingCtx.licenseId);
        if (sameLicense) {
          this.persistResolvedAccess(sameLicense);
          return sameLicense.licenseId;
        }
      }

      // 2) Tentar casar com entidade do contexto salvo.
      const entityId = this.readContextEntityId();
      if (entityId !== null) {
        const byEntity = valid.find(a => Number(a.values?.entity) === entityId);
        if (byEntity) {
          this.persistResolvedAccess(byEntity);
          return byEntity.licenseId;
        }
      }

      // 3) Fallback: primeiro acesso válido.
      const first = valid[0];
      this.persistResolvedAccess(first);
      return first.licenseId;
    } catch {
      return null;
    }
  }

  private persistResolvedAccess(access: UserAccessListItem): void {
    localStorage.setItem(STORAGE_KEYS.BRIDGE_USER_ACCESS, access.licenseId);
    if (access.id) {
      localStorage.setItem(
        STORAGE_KEYS.BRIDGE_ACCESS_PAIR,
        JSON.stringify({ licenseId: access.licenseId, accessId: access.id })
      );
    }
    localStorage.setItem(
      STORAGE_KEYS.BRIDGE_USER_ACCESS_CONTEXT,
      JSON.stringify({
        licenseId: access.licenseId,
        accessId: access.id,
        admin: Boolean((access as any).admin ?? (access as any).values?.admin)
      })
    );
  }

  /**
   * Lê e parseia o contexto de acesso do usuário (`licenseId`, `admin`) do
   * localStorage. Único parser dessa chave — `PermissionsService.isAdmin()`
   * reusa este método em vez de reimplementar o parse.
   */
  readUserAccessContext(): { licenseId?: string; admin?: boolean } | null {
    try {
      // Fallback de leitura na chave antiga (__pwa_bridge_user_access_context) por 1 release — TODO remover
      const raw = localStorage.getItem(STORAGE_KEYS.BRIDGE_USER_ACCESS_CONTEXT) ||
        localStorage.getItem(LEGACY_STORAGE_KEYS.BRIDGE_USER_ACCESS_CONTEXT);
      if (!raw) {
        return null;
      }
      return JSON.parse(raw) as { licenseId?: string; admin?: boolean };
    } catch {
      return null;
    }
  }

  private readContextEntityId(): number | null {
    try {
      // Fallback de leitura na chave antiga (__pwa_bridge_contexto_completo) por 1 release — TODO remover
      const raw = localStorage.getItem(STORAGE_KEYS.BRIDGE_CONTEXTO_COMPLETO) ||
        localStorage.getItem(LEGACY_STORAGE_KEYS.BRIDGE_CONTEXTO_COMPLETO);
      if (!raw) {
        return null;
      }
      const parsed = JSON.parse(raw) as { entidade?: { id?: number | string } };
      const id = parsed?.entidade?.id;
      if (id === undefined || id === null) {
        return null;
      }
      const n = Number(id);
      return Number.isNaN(n) ? null : n;
    } catch {
      return null;
    }
  }

  /** Id de acesso no user-accounts (24 hex) vs licenseId (UUID etc.). */
  private isAccessDocumentId(value: string): boolean {
    return /^[a-f0-9]{24}$/i.test(value);
  }

  private readAccessPair(): { licenseId: string; accessId: string } | null {
    try {
      // Fallback de leitura na chave antiga (__pwa_bridge_access_pair) por 1 release — TODO remover
      const raw = localStorage.getItem(STORAGE_KEYS.BRIDGE_ACCESS_PAIR) ||
        localStorage.getItem(LEGACY_STORAGE_KEYS.BRIDGE_ACCESS_PAIR);
      if (!raw) {
        return null;
      }
      const o = JSON.parse(raw) as { licenseId?: string; accessId?: string };
      if (o.licenseId && o.accessId) {
        return { licenseId: o.licenseId, accessId: o.accessId };
      }
    } catch {
      /* ignore */
    }
    return null;
  }

  private async resolveAccessId(userAccessOrLicense: string): Promise<string | null> {
    if (this.isAccessDocumentId(userAccessOrLicense)) {
      return userAccessOrLicense;
    }

    const cached = this.resolvedAccessIdByLicense.get(userAccessOrLicense);
    if (cached) {
      return cached;
    }

    const pair = this.readAccessPair();
    if (pair && pair.licenseId === userAccessOrLicense) {
      this.resolvedAccessIdByLicense.set(userAccessOrLicense, pair.accessId);
      return pair.accessId;
    }

    const base = this.getUserAccountsBaseUrl();
    const listUrl = `${base}/api/suite/users/@me/access`;
    try {
      const list = await firstValueFrom(this.http.get<UserAccessListItem[]>(listUrl));
      const match = list?.find(a => a.licenseId === userAccessOrLicense);
      if (match?.id) {
        this.resolvedAccessIdByLicense.set(userAccessOrLicense, match.id);
        // Persistir par accessId/licenseId e flag admin — main.ts limpa essas chaves
        // no re-login e sem isso isAdmin() retorna false mesmo para administradores.
        this.persistResolvedAccess(match as UserAccessListItem);
        return match.id;
      }
    } catch (_e) {
      /* falha ao resolver */
    }

    return null;
  }

  private normalizeResponse(response: unknown): PermissionEntry[] | null {
    if (Array.isArray(response)) {
      return response as PermissionEntry[];
    }

    if (response && typeof response === 'object') {
      const content = (response as { content?: unknown }).content;
      if (Array.isArray(content)) {
        return content as PermissionEntry[];
      }
    }

    return null;
  }

  private getUserAccess(): string | null {
    // PRIORIDADE 1: _userAccess na URL (redirect do AngularJS após login/re-login via TWA) —
    // representa a sessão recém-autenticada, e o guard roda antes do pwa-initialization
    // salvar esse valor no localStorage (que só ocorre no ngOnInit).
    const params = new URLSearchParams(window.location.search);
    const fromUrl = params.get('_userAccess');
    if (fromUrl) {
      const decoded = decodeURIComponent(fromUrl);
      // Persiste já, pra chamadas subsequentes não precisarem reler a URL
      try {
        localStorage.setItem(STORAGE_KEYS.BRIDGE_USER_ACCESS, decoded);
      } catch (_e) { /* ignorar falha de storage */ }
      return decoded;
    }

    // PRIORIDADE 2: localStorage (salvo pelo pwa-initialization)
    // Fallback de leitura na chave antiga (__pwa_bridge_user_access) por 1 release — TODO remover
    const fromBridge = localStorage.getItem(STORAGE_KEYS.BRIDGE_USER_ACCESS) ||
      localStorage.getItem(LEGACY_STORAGE_KEYS.BRIDGE_USER_ACCESS);
    if (fromBridge) {
      return fromBridge;
    }

    return null;
  }

  private getUserAccountsBaseUrl(): string {
    const hostname = window.location.hostname;
    const base = (hostname.includes('localhost') || hostname.includes('127.0.0.1') || hostname.includes('test.betha.cloud'))
      ? 'https://plataforma-autorizacoes.test.betha.cloud'
      : 'https://plataforma-autorizacoes.betha.cloud';
    return `${base}/user-accounts/v0.1`;
  }
}
